import { createFileRoute, Link } from "@tanstack/react-router";
import { Container, Eyebrow } from "@/components/site/primitives";
import { SITE } from "@/lib/site-content";
import { ogImageMeta } from "@/lib/og";

const title = "Terms and privacy | Scryla";
const description =
  "Terms of use and privacy note for scryla.co.uk: what enquiry forms and the service finder store, who processes it, cookies, and your UK GDPR rights.";
const UPDATED = "20 October 2026";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE.url}/terms` },
      ...ogImageMeta("terms", "Terms of service | Scryla"),
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/terms` }],
  }),
  component: TermsPage,
});

function Section({ id, heading, children }: { id: string; heading: string; children: React.ReactNode }) {
  return (
    <section aria-labelledby={`${id}-heading`} id={id}>
      <h2 id={`${id}-heading`} className="text-xl font-bold tracking-[-0.02em]">
        {heading}
      </h2>
      <div className="mt-3 flex flex-col gap-3">{children}</div>
    </section>
  );
}

function TermsPage() {
  const mail = (
    <a href={`mailto:${SITE.email}`} className="font-semibold hover:text-primary">
      {SITE.email}
    </a>
  );
  return (
    <section aria-labelledby="terms-heading">
      <Container className="max-w-[820px]">
        <Eyebrow>Legal</Eyebrow>
        <h1 id="terms-heading" className="mt-4 text-[34px] leading-[1.06] font-bold tracking-[-0.035em] md:text-5xl">
          Terms and privacy
        </h1>
        <p className="mt-4 text-sm text-muted-foreground">Last updated {UPDATED}</p>

        <div className="mt-10 flex flex-col gap-8 text-base leading-relaxed text-body">
          <Section id="who-runs-this-site" heading="Who runs this site">
            <p>
              This site is operated by Scryla Consultancy Ltd (company number 08891066), {SITE.location}.
              Scryla is the data controller for information you send through it and is registered
              with the Information Commissioner's Office, registration number ZA865326. Contact us
              at {mail}.
            </p>
          </Section>

          <Section id="use-of-this-site" heading="Use of this site">
            <p>
              The content here is general information about the services Timothy Ng and Scryla
              provide. It is not professional advice for your situation, and no client relationship
              is created by reading it, using the service finder, or sending an enquiry.
            </p>
          </Section>

          <Section id="contact-form-data" heading="Enquiry forms">
            <p>
              When you send a note through any enquiry form on the site, we store the name, email
              address, company and message you submit, and which page it came from, and Tim receives
              a copy by email. We use it only to reply to your enquiry. It is not sold or shared for
              marketing. Ask us to delete it at any time and we will.
            </p>
          </Section>

          <Section id="service-finder" heading="Service finder">
            <p>
              The description you type into the service finder is sent to an AI model, through our
              hosting provider's AI gateway, to suggest a relevant Scryla service. Scryla does not
              save the description when you only ask for a suggestion.
            </p>
            <p>
              To stop misuse, each visitor can ask up to three times every ten minutes. To count
              this we set a functional cookie (scryla_finder_visitor, lasting 24 hours) and keep a
              one-way scrambled form of your network address for a short time. Neither is used for
              analytics or advertising.
            </p>
            <p>
              If you choose "Email me this recommendation" and tick the consent box, we store your
              name, email, optional company, your description, the suggestion, and the time you
              agreed. We send you one email with the recommendation and Tim a copy so he can reply.
              You are not added to any mailing list.
            </p>
          </Section>

          <Section id="processors" heading="Who processes data for us">
            <ul className="list-disc space-y-1 pl-6">
              <li>Our website hosting provider, which also provides the AI gateway used by the service finder.</li>
              <li>Supabase, which stores enquiries and service finder email requests.</li>
              <li>Resend, which delivers notification and recommendation emails.</li>
              <li>Cloudflare Turnstile, which checks forms for spam.</li>
              <li>Google Analytics and Microsoft Clarity, only if you accept analytics cookies.</li>
            </ul>
            <p>Some of these providers may process data outside the UK under their own safeguards.</p>
          </Section>

          <Section id="cookies-and-analytics" heading="Cookies and analytics">
            <p>
              This site uses Google Analytics and Microsoft Clarity to understand which pages are
              useful. Analytics storage is denied by default and neither tool is loaded, and no
              analytics cookie is set, until you accept on the cookie banner. No advertising,
              remarketing or personalisation storage is used at any point. You can change your
              choice at any time using the &ldquo;Cookie settings&rdquo; link in the footer.
            </p>
          </Section>

          <Section id="lawful-basis" heading="Lawful basis and your rights">
            <p>
              We reply to enquiries on the basis of our legitimate interest in responding to people
              who contact us. Emailing a service finder recommendation and analytics cookies rely on
              your consent, which you can withdraw at any time.
            </p>
            <p>
              Under UK GDPR you can ask to see, correct or delete the information we hold about you,
              or object to how we use it. Email {mail}. If you are unhappy with our response you can
              complain to the{" "}
              <a href="https://ico.org.uk/make-a-complaint/" className="font-semibold underline underline-offset-4">
                Information Commissioner's Office
              </a>
              .
            </p>
          </Section>

          <Section id="liability" heading="Liability">
            <p>
              We take care to keep this site accurate and available, but we do not warrant that it
              is error free or uninterrupted, and we are not liable for loss arising from reliance
              on general information or AI-generated suggestions published here.
            </p>
          </Section>

          <p className="text-sm">
            See also our <Link to="/accessibility" className="font-semibold underline underline-offset-4">accessibility statement</Link>.
          </p>
        </div>
      </Container>
    </section>
  );
}
